// frontend: src/components/Home.tsx

import { useEffect, useRef, useState } from "react";
import NavigationBar from "./NavigationBar";
import { openWS } from "../lib/ws";
import { apiFetch } from "../lib/api";
import Hls from "hls.js";

function toIdList(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  return input
    .map((c) => (typeof c === "string" ? c : (c && (c as any).id)))
    .filter((id): id is string => Boolean(id));
}

/** ---------- One HLS tile in the grid ---------- **/
function getGridCols(count: number): string {
  if (count <= 1) return "grid-cols-1";
  if (count <= 4) return "grid-cols-2";
  if (count <= 9) return "grid-cols-3";
  return "grid-cols-4";
}

function GridHlsTile({ cameraId }: { cameraId: string }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const refreshTimer = useRef<number | null>(null);
  const [ready, setReady] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const visRef = useRef<boolean>(false);
  const destroyRef = useRef<(() => void) | null>(null);


  async function fetchUrl() {
    const res = await apiFetch(
      `/api/kvs/hls-url?stream=${encodeURIComponent(cameraId)}&mode=LIVE&max_frag=3`
    );
    if (!res.ok) throw new Error("hls-url fetch failed");
    const { url } = await res.json();
    return url as string;
  }

  function clearRefresh() {
    if (refreshTimer.current) {
      window.clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }

  function scheduleRefresh(ms: number) {
    clearRefresh();
    refreshTimer.current = window.setTimeout(() => {
      // soft reload of the session
      startHls(true).catch(() => {
        /* noop */
      });
    }, ms) as unknown as number;
  }

  async function startHls(isRefresh = false) {
    if (!visRef.current) return; // start only when visible
    const video = videoRef.current;
    if (!video) return;

    // Fresh session URL
    const url = await fetchUrl();

    // Safari/iOS: native HLS
    if (!Hls.isSupported()) {
      return new Promise<void>((resolve) => {
        const onCanPlay = () => {
          setReady(true);
          video.removeEventListener("canplay", onCanPlay);
          resolve();
        };
        video.addEventListener("canplay", onCanPlay, { once: true });
        video.src = url;
        video.muted = true;
        video.playsInline = true;
        (video as any).autoplay = true;
        video.play().catch(() => {});
        scheduleRefresh(4 * 60_000); // ~4 min; session TTL is ~5 min
      });
    }

    // Others: hls.js
    // On refresh, re-use existing instance if present, otherwise create.
    let hls = hlsRef.current;
    if (!hls) {
      hls = new Hls({
        enableWorker: true,
        liveBackBufferLength: 30,
      });
      hlsRef.current = hls;

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setReady(true);
        setReconnecting(false);
        video.play().catch(() => {});
      });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data?.fatal || data?.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR) {
          setReady(false);
          setReconnecting(true);
          startHls(true).catch(() => {});
        }
      });
      hls.attachMedia(video);
    }

    // load the (new) source
    hls.loadSource(url);
    video.muted = true;
    video.playsInline = true;
    (video as any).autoplay = true;
    scheduleRefresh(4 * 60_000);

    // Provide a clean destroy function
    destroyRef.current = () => {
      clearRefresh();
      try {
        hlsRef.current?.destroy();
      } catch {}
      hlsRef.current = null;
      try {
        video.removeAttribute("src");
        video.load();
      } catch {}
      setReady(false);
    };
  }

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    // Lazy start: observe visibility
    const obs = new IntersectionObserver(
      (entries) => {
        const vis = entries.some((e) => e.isIntersecting);
        visRef.current = vis;

        if (vis) {
          // (Re)start if we don't have an active player
          if (!hlsRef.current && el.readyState === 0) {
            startHls().catch(() => {});
          }
        } else {
          // Pause when off-screen (keep instance, cheaper than full destroy)
          try {
            el.pause();
          } catch {}
        }
      },
      { root: null, rootMargin: "200px", threshold: 0.01 } // start slightly before visible
    );

    obs.observe(el);

    return () => {
      obs.disconnect();
    };
  }, []);

  useEffect(() => {
    // Full cleanup on unmount
    return () => {
      clearRefresh();
      try {
        hlsRef.current?.destroy();
      } catch {}
      hlsRef.current = null;
    };
  }, []);

  return (
    <div
      className="relative aspect-video border-2 border-black overflow-hidden hover:scale-105 transition-transform duration-300 cursor-pointer bg-black"
      title={`Open ${cameraId}`}
    >
      <video
        ref={videoRef}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-150 ${
          ready ? "opacity-100" : "opacity-60"
        }`}
        playsInline
        muted
      />
      {!ready && !reconnecting && (
        <div className="absolute inset-0 flex items-center justify-center text-gray-400">
          Waiting for signal…
        </div>
      )}
      {reconnecting && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
          <div className="flex flex-col items-center gap-2">
            <div className="w-5 h-5 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-yellow-400 text-xs font-medium tracking-wide">
              Reconnecting…
            </span>
          </div>
        </div>
      )}
      <div className="absolute top-1 left-2 text-xs text-white/80 pointer-events-none">
        {cameraId}
      </div>
    </div>
  );
}

/** ---------- Page ---------- **/
const Home = () => {
  const [activeCameras, setActiveCameras] = useState<string[]>([]);

  // Camera list: initial + live
  useEffect(() => {
    let ws: WebSocket | undefined;

    const fetchInitial = async () => {
      try {
        const res = await apiFetch("/api/cameras/active");
        if (!res.ok) {
          setActiveCameras([]);
          return;
        }
        const data = await res.json();
        setActiveCameras(toIdList(data));
      } catch (e) {
        console.error("fetch /api/cameras/active failed:", e);
        setActiveCameras([]);
      }
    };

    fetchInitial();

    ws = openWS("/active_cameras");
    ws.onmessage = (e) => {
      try {
        const payload = JSON.parse(e.data);
        if (payload && Array.isArray(payload.cameras)) {
          setActiveCameras(toIdList(payload.cameras));
        }
      } catch {}
    };
    ws.onerror = (e) => console.warn("active_cameras WS error", e as any);
    ws.onclose = () => console.warn("Active cameras WebSocket closed");

    return () => {
      try {
        ws?.close();
      } catch {}
    };
  }, []);

  // Open large HLS popup on click (reusing the tile endpoint)
  const openStreamPopup = async (cameraId: string) => {
    const width = 960,
      height = 540;
    const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);

    const win = window.open(
      "",
      `_cam_${cameraId}`,
      `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes`
    );
    if (!win) return;

    win.document.write(`
      <!doctype html><html><head><meta charset="utf-8"/>
      <title>${cameraId} – Live</title>
      <style>
        html,body{margin:0;height:100%;background:#000}
        video{width:100%;height:100%;background:#000}
      </style></head>
      <body><video id="v" playsinline controls></video></body></html>
    `);
    win.document.close();

    const video = win.document.getElementById("v") as HTMLVideoElement;
    const res = await apiFetch(
      `/api/kvs/hls-url?stream=${encodeURIComponent(cameraId)}&mode=LIVE&max_frag=3`
    );
    if (!res.ok) return;
    const { url } = await res.json();

    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, liveBackBufferLength: 30 });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));

      const refresh = async () => {
        try {
          const r = await apiFetch(
            `/api/kvs/hls-url?stream=${encodeURIComponent(
              cameraId
            )}&mode=LIVE&max_frag=3`
          );
          if (!r.ok) return;
          const { url: newUrl } = await r.json();
          hls.loadSource(newUrl);
          hls.attachMedia(video);
        } catch {}
      };

      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data?.fatal || data?.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR) {
          refresh();
        }
      });

      win.addEventListener("beforeunload", () => {
        try {
          hls.destroy();
        } catch {}
      });
    } else {
      video.src = url;
      video.play().catch(() => {});
    }
  };

  return (
    <div className="bg-black min-h-screen text-white">
      <NavigationBar />
      <div className="p-4">
        {activeCameras.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No cameras online
            </div>
        ) : (
            <div className={`grid ${getGridCols(activeCameras.length)} gap-4`}>
              {activeCameras.map((cameraId) => (
                  <div key={cameraId} onClick={() => openStreamPopup(cameraId)}>
                    <GridHlsTile cameraId={cameraId}/>
                  </div>
              ))}
              {Array.from({
                length: Math.max(0,
                    activeCameras.length <= 1 ? 1 :
                        activeCameras.length <= 4 ? 4 :
                            activeCameras.length <= 9 ? 9 :
                                Math.ceil(activeCameras.length / 4) * 4
                                - activeCameras.length
                )
              }).map((_, i) => (
                  <div
                      key={`placeholder-${i}`}
                      className="border border-gray-800 flex items-center justify-center text-gray-700 aspect-video"
                  >
                    —
                  </div>
              ))}
            </div>
        )}
        <div className="mt-3 text-xs text-gray-600 text-right">
          {activeCameras.length} camera{activeCameras.length !== 1 ? "s" : ""} online
        </div>
      </div>
    </div>
  );
};

export default Home;
